import { useMemo, useState } from "react";
import { Plus, Trash2, FileDown, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "./ClientesModule";
import { ReciboDocumento, descargarReciboPdf } from "./ReciboCobro";

const inputCls = "w-full h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-vteal-light/50";
const emptyItem = { concepto: "", monto: "" };
const hoyStr = () => new Date().toISOString().slice(0, 10);
const genNumero = (fecha) => `RC-${(fecha || hoyStr()).replaceAll("-", "")}-${Math.random().toString(16).slice(2, 6).toUpperCase()}`;

export default function ManualRecibo() {
  const [form, setForm] = useState({ client_name: "", client_ced: "", client_email: "", client_code: "", fecha: hoyStr(), invoice: false, currency: "CRC", recurrente: false, frecuencia: "mensual" });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sufijo, setSufijo] = useState(() => Math.random().toString(16).slice(2, 6).toUpperCase());

  const validItems = useMemo(() => items.filter((i) => i.concepto.trim() && Number(i.monto) > 0), [items]);
  const subtotal = validItems.reduce((a, i) => a + Number(i.monto), 0);
  const iva = form.invoice ? subtotal * 0.13 : 0;
  const clientCode = form.client_code.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
  const numero = `VCR-${form.fecha.replaceAll("-", "")}-${clientCode || sufijo}`;

  const d = {
    numero,
    emitido: fmtDate(hoyStr()),
    company: form.client_name,
    ced: form.client_ced,
    client_email: form.client_email,
    items: validItems.map((i) => ({ concepto: i.concepto.trim(), fecha: fmtDate(form.fecha), monto: Number(i.monto) })),
    invoice: form.invoice,
    currency: form.currency,
    iva,
    total: subtotal + iva,
  };

  const setItem = (idx, key, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));

  const reset = () => {
    setForm({ client_name: "", client_ced: "", client_email: "", client_code: "", fecha: hoyStr(), invoice: false, currency: "CRC", recurrente: false, frecuencia: "mensual" });
    setItems([{ ...emptyItem }]);
    setSufijo(Math.random().toString(16).slice(2, 6).toUpperCase());
  };

  const validar = () => {
    if (!form.client_name.trim()) return "Ingrese el nombre del cliente.";
    if (validItems.length === 0) return "Agregue al menos un rubro con concepto y monto.";
    return null;
  };

  const payloadBase = () => ({
    client_name: form.client_name,
    client_ced: form.client_ced,
    client_email: form.client_email,
    fecha: form.fecha,
    invoice: form.invoice,
    numero,
    items: validItems.map((i) => ({ concepto: i.concepto.trim(), monto: Number(i.monto) })),
    currency: form.currency,
    recurrente: form.recurrente,
    frecuencia: form.frecuencia,
  });

  const downloadPdf = async () => {
    const err = validar();
    if (err) { toast.error(err); return; }
    setDownloading(true);
    try {
      await api.post("/admin/manual-receipt/save", payloadBase());
      await descargarReciboPdf("recibo-manual", numero);
      toast.success("PDF descargado y recibo registrado en Historial Recibos.");
    } catch (err2) {
      toast.error(formatApiErrorDetail(err2.response?.data?.detail) || "No se pudo registrar el recibo.");
    } finally {
      setDownloading(false);
    }
  };

  const send = async () => {
    const err = validar();
    if (err) { toast.error(err); return; }
    if (!form.client_email.trim()) { toast.error("Ingrese el correo del cliente para enviar el recibo."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/manual-receipt", payloadBase());
      toast.success(`Recibo enviado a ${data.sent_to} y registrado en Historial Recibos.`);
    } catch (err2) {
      toast.error(formatApiErrorDetail(err2.response?.data?.detail) || "No se pudo enviar el recibo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6" data-testid="manual-receipt">
      {/* Formulario */}
      <div className="rounded-xl bg-vnavy-800 border border-white/10 p-6 space-y-4 self-start">
        <p className="text-[11px] uppercase tracking-[0.15em] text-vteal-light font-semibold">Recibo de cobro manual</p>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Nombre del cliente / empresa *</label>
          <input data-testid="manual-receipt-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Ej. Empresa S.A." className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Cédula jurídica</label>
            <input data-testid="manual-receipt-ced" value={form.client_ced} onChange={(e) => setForm({ ...form, client_ced: e.target.value })} placeholder="3-101-000000" className={inputCls} />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Fecha de cobro *</label>
            <input type="date" data-testid="manual-receipt-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Correo del cliente (para envío) *</label>
          <input type="email" data-testid="manual-receipt-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="correo@empresa.com" className={inputCls} />
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Código de facturación del cliente</label>
          <input data-testid="manual-receipt-code" value={form.client_code} maxLength={12}
            onChange={(e) => setForm({ ...form, client_code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") })}
            placeholder="Ej. AA93" className={inputCls} />
          <p className="text-gray-600 text-[10px] mt-1">Se usa en el N° del recibo: VCR-fecha-código</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-gray-400">Rubros del cobro *</label>
            <button onClick={() => setItems([...items, { ...emptyItem }])} data-testid="manual-receipt-add-item"
              className="flex items-center gap-1 text-xs text-vteal-light hover:text-white transition-colors">
              <Plus className="h-3.5 w-3.5" /> Agregar rubro
            </button>
          </div>
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
                <input value={item.concepto} onChange={(e) => setItem(i, "concepto", e.target.value)} placeholder={`Concepto del rubro ${i + 1}`}
                  data-testid={`manual-receipt-item-concepto-${i}`} className={inputCls} />
                <div className="flex gap-2">
                  <input type="number" min="0" value={item.monto} onChange={(e) => setItem(i, "monto", e.target.value)} placeholder="₡ Monto"
                    data-testid={`manual-receipt-item-monto-${i}`} className={`${inputCls} flex-1`} />
                  <button onClick={() => setItems(items.filter((_, j) => j !== i))} disabled={items.length === 1}
                    data-testid={`manual-receipt-item-del-${i}`}
                    className="p-2 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Moneda del recibo *</label>
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            {[{ v: "CRC", label: "₡ Colones" }, { v: "USD", label: "$ Dólares" }].map((m) => (
              <button key={m.v} type="button" onClick={() => setForm({ ...form, currency: m.v })}
                data-testid={`manual-receipt-currency-${m.v.toLowerCase()}`}
                className={`flex-1 text-xs py-2 font-semibold transition-colors ${form.currency === m.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2.5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">Factura electrónica</p>
            <p className="text-gray-600 text-xs">Sumar 13% IVA al cobro</p>
          </div>
          <Switch checked={form.invoice} onCheckedChange={(v) => setForm({ ...form, invoice: v })} data-testid="manual-receipt-invoice" className="data-[state=checked]:bg-vteal-light" />
        </div>

        <div className="rounded-lg border border-white/10 px-3 py-2.5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">Cobro frecuente</p>
              <p className="text-gray-600 text-xs">Reenviar este recibo automáticamente en cada período</p>
            </div>
            <Switch checked={form.recurrente} onCheckedChange={(v) => setForm({ ...form, recurrente: v })} data-testid="manual-receipt-recurrente" className="data-[state=checked]:bg-vteal-light" />
          </div>
          {form.recurrente && (
            <div className="flex rounded-lg overflow-hidden border border-white/10" data-testid="manual-receipt-frecuencia">
              {[{ v: "mensual", label: "Mensual (mismo día)" }, { v: "quincenal", label: "Quincenal (día y +15)" }].map((f) => (
                <button key={f.v} type="button" onClick={() => setForm({ ...form, frecuencia: f.v })}
                  data-testid={`manual-receipt-frecuencia-${f.v}`}
                  className={`flex-1 text-xs py-2 px-1 font-semibold transition-colors ${form.frecuencia === f.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={downloadPdf} disabled={downloading} data-testid="manual-receipt-pdf"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-vteal-light/40 text-vteal-light text-sm font-semibold hover:bg-vteal-light/10 transition-colors disabled:opacity-60">
            <FileDown className="h-4 w-4" /> {downloading ? "Generando..." : "Descargar PDF"}
          </button>
          <button onClick={send} disabled={sending} data-testid="manual-receipt-send"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            <Send className="h-4 w-4" /> {sending ? "Enviando..." : "Enviar"}
          </button>
          <button onClick={reset} data-testid="manual-receipt-reset"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full text-gray-500 text-sm hover:text-white transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Limpiar
          </button>
        </div>
      </div>

      {/* Vista previa */}
      <div className="overflow-x-auto rounded-xl">
        <ReciboDocumento d={d} id="recibo-manual" />
      </div>
    </div>
  );
}
